import { getActivos, createActivo, updateActivo, deleteActivo, getActivoAtributos } from '../models/activoModel.js';
import Swal from 'sweetalert2';
import pb from '../lib/pocketbase';
import { toasts } from 'svelte-toasts';

// Variable global para el timeout de actualización
let activoUpdateTimeout;

// Obtener lista de activos
export async function fetchActivos(page, perPage) {
  try {
    const response = await getActivos(page, perPage);
    
    // Ensure tipo data is properly expanded for display while preserving the ID
    // and fetch first gallery image for each activo
    const activosWithExpandedTipo = await Promise.all(response.items.map(async (activo) => {
      let expandedActivo = { ...activo };
      
      if (activo.expand && activo.expand.tipo) {
        expandedActivo = {
          ...expandedActivo,
          tipo: activo.expand.tipo,
          tipoId: activo.tipo // Preserve the original ID for form operations
        };
      }
      
      // Fetch first gallery image
      try {
        const firstImage = await fetchFirstGalleryImage(activo.id);
        expandedActivo.firstImage = firstImage;
      } catch (error) {
        console.warn('Error fetching first image for activo:', activo.id, error);
        expandedActivo.firstImage = null;
      }
      
      return expandedActivo;
    }));
    
    return {
      activo: activosWithExpandedTipo,
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    // Handle auto-cancellation errors gracefully
    if (error.message && error.message.includes('autocancelled')) {
      console.warn('Fetch request was auto-cancelled, this is normal for rapid updates');
      return {
        activo: [],
        totalRecords: 0,
        currentPage: page,
      };
    }
    console.error("Error fetching activos:", error);
    Swal.fire('Error', 'No se pudieron cargar los activos', 'error');
    return null;
  }
}

// Guardar activo (crear o actualizar)
export async function saveActivo(activo, currentActivoId) {
  try {
    let activoId;

    if (currentActivoId) {
      await updateActivo(currentActivoId, activo);
      activoId = currentActivoId;
      toasts.success('Activo actualizado correctamente');
    } else {
      const newActivo = await createActivo(activo);
      activoId = newActivo.id;
      toasts.success('Activo creado correctamente');
    }

    // Llamar a saveAtributosActivo después de guardar el activo
    if (activo['atributos'] && activo['atributos'].length > 0) {
      console.log('activo:', activo);
      await saveAtributosActivo(activoId, activo['atributos']);
    }

    return true;
  } catch (error) {
    console.error("Error saving activo:", error);
    Swal.fire('Error', 'No se pudo guardar el activo', 'error');
    return false;
  }
}

// Eliminar activo
export async function removeActivo(id) {
  try {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "Esta acción no se puede deshacer",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      await deleteActivo(id);
      toasts.success('Activo eliminado correctamente');
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error removing activo:", error);
    Swal.fire('Error', 'No se pudo eliminar el activo', 'error');
    return false;
  }
}

// Suscripción en tiempo real a cambios en la colección 'activo'
export function subscribeToActivos(searchQuery, handleSearch) {
  pb.collection('activo').subscribe('*', async (e) => {
    console.log('Evento en tiempo real recibido:', e);

    // Clear any pending update
    if (activoUpdateTimeout) {
      clearTimeout(activoUpdateTimeout);
    }

    // Debounce the update to prevent rapid successive calls
    activoUpdateTimeout = setTimeout(async () => {
      try {
        await handleSearch(); // Actualiza la lista manteniendo la página actual
      } catch (error) {
        console.warn('Error en actualización en tiempo real:', error);
        // Don't show error to user for real-time updates
      }
    }, 300); // Wait 300ms before updating
  });
}

// Cancelar suscripción
export function unsubscribeFromActivos() {
  // Clear any pending update timeout
  if (activoUpdateTimeout) {
    clearTimeout(activoUpdateTimeout);
    activoUpdateTimeout = null;
  }
  pb.collection('activo').unsubscribe('*');
}

// Buscar activos por descripción, código, nombre, precio
export async function searchActivos(query, page = 1, perPage = 10) {
  try {
    // If query is empty, return empty results
    if (!query || query.trim() === '') {
      return {
        activo: [],
        totalRecords: 0,
        currentPage: page,
      };
    }
    
    // Escape special characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    const filter = `descrip ~ "${escapedQuery}" || codigo ~ "${escapedQuery}" || nombre ~ "${escapedQuery}" || precio ~ "${escapedQuery}"`;
    
    const response = await pb.collection('activo').getList(page, perPage, {
      filter: filter,
      expand: 'tipo',
      $autoCancel: false // Disable auto-cancellation for search requests
    });
    
    // Ensure tipo data is properly expanded for display while preserving the ID
    // and fetch first gallery image for each activo
    const activosWithExpandedTipo = await Promise.all(response.items.map(async (activo) => {
      let expandedActivo = { ...activo };
      
      if (activo.expand && activo.expand.tipo) {
        expandedActivo = {
          ...expandedActivo,
          tipo: activo.expand.tipo,
          tipoId: activo.tipo // Preserve the original ID for form operations
        };
      }
      
      // Fetch first gallery image
      try {
        const firstImage = await fetchFirstGalleryImage(activo.id);
        expandedActivo.firstImage = firstImage;
      } catch (error) {
        console.warn('Error fetching first image for activo:', activo.id, error);
        expandedActivo.firstImage = null;
      }
      
      return expandedActivo;
    }));
    
    return {
      activo: activosWithExpandedTipo,
      totalRecords: response.totalItems,
      currentPage: response.currentPage,
    };
  } catch (error) {
    // Handle auto-cancellation errors gracefully
    if (error.message && error.message.includes('autocancelled')) {
      console.warn('Search request was auto-cancelled, this is normal for rapid updates');
      return {
        activo: [],
        totalRecords: 0,
        currentPage: page,
      };
    }
    console.error("Error searching activos:", error);
    throw error;
  }
}

// Obtener tipos para el dropdown
export async function getTiposForDropdown() {
  try {
    const response = await pb.collection('tipo').getList(1, 1000, {
      sort: 'descrip',
      $autoCancel: false
    });
    return response.items.map(tipo => ({
      key: tipo.id,
      value: tipo.descrip
    }));
  } catch (error) {
    console.error("Error fetching tipos for dropdown:", error);
    return [];
  }
}

export async function fetchAtributosActivo(id) {
  try {
    const response = await getActivoAtributos(id);
    return {
      atributos: response,
    };
  } catch (error) {
    console.error("Error fetching activo atributos:", error);
    Swal.fire('Error', 'No se pudieron cargar los atributos del activo', 'error');
    return null;
  }
}

// Fetch first gallery image for an activo
export async function fetchFirstGalleryImage(activoId) {
  try {
    const response = await pb.collection('activo_gallery').getList(1, 1, {
      filter: `activo="${activoId}"`,
      sort: '-created',
      $autoCancel: false
    });
    
    if (response.items && response.items.length > 0) {
      const galleryItem = response.items[0];
      return {
        id: galleryItem.id,
        url: galleryItem.url,
        collectionId: galleryItem.collectionId
      };
    }
    return null;
  } catch (error) {
    console.warn('Error fetching first gallery image for activo:', activoId, error);
    return null;
  }
}

export async function saveAtributosActivo(activoId, atributos) {
  // Elimina los atributos existentes antes de guardar los nuevos
  await deleteAtributosActivo(activoId);
  
  try {
    // Itera sobre cada atributo y guarda/actualiza en la colección 'atributo_activo'
    for (const { atributo, valor } of atributos) {
      await pb.collection('atributo_activo').create({
        activo: activoId,
        atributo: atributo.id,
        valor,
      });
    }

    toasts.success('Atributos actualizados exitosamente');
  } catch (error) {
    console.error('Error guardando atributos:', error);
    toasts.error('No se pudieron guardar los atributos');
  }
}

export async function deleteAtributosActivo(activoId) {
  try {
    // Recupera los atributos del activo
    const response = await pb.collection('atributo_activo').getList(1, 50, {
      filter: `activo="${activoId}"`,
    });

    // Elimina cada atributo individualmente
    const deletePromises = response.items.map((item) =>
      pb.collection('atributo_activo').delete(item.id)
    );

    await Promise.all(deletePromises);
    console.log('Atributos eliminados correctamente');
  } catch (error) {
    console.error('Error eliminando atributos del activo:', error);
    throw new Error('No se pudieron eliminar los atributos');
  }
}

// Import activos from CSV
export async function importActivosFromCSV(csvData) {
  try {
    const lines = csvData.split('\n').filter(line => line.trim() !== '');
    const headers = lines[0].split(',').map(h => h.trim());
    
    // Find the indices of the required columns
    const codigoIndex = headers.findIndex(h => h.toLowerCase().includes('sku') || h.toLowerCase().includes('code'));
    const nombreIndex = headers.findIndex(h => h.toLowerCase().includes('name'));
    const descripIndex = headers.findIndex(h => h.toLowerCase().includes('description') || h.toLowerCase().includes('descrip'));
    const precioIndex = headers.findIndex(h => h.toLowerCase().includes('price'));
    const tipoIndex = headers.findIndex(h => h.toLowerCase().includes('type'));
    
    if (codigoIndex === -1 || descripIndex === -1) {
      throw new Error('CSV debe contener al menos las columnas SKU/CODE y DESCRIPTION');
    }

    // Get all tipos to map TYPE ID to actual tipo ID
    const tiposResponse = await pb.collection('tipo').getList(1, 1000, {
      $autoCancel: false
    });
    const tiposMap = {};
    tiposResponse.items.forEach(tipo => {
      tiposMap[tipo.descrip] = tipo.id;
    });

    // Get all existing attributes to avoid duplicates
    const atributosResponse = await pb.collection('atributo').getList(1, 1000, {
      $autoCancel: false
    });
    const atributosMap = {};
    atributosResponse.items.forEach(atributo => {
      atributosMap[atributo.descrip] = atributo.id;
    });

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    // Process each line (skip header)
    for (let i = 1; i < lines.length; i++) {
      let values = []; // Declare values outside try block
      try {
        values = lines[i].split(',').map(v => v.trim());
        
        if (values.length < headers.length) {
          continue; // Skip incomplete rows
        }

        const codigo = values[codigoIndex] || '';
        const nombre = nombreIndex !== -1 ? values[nombreIndex] || '' : '';
        const descrip = values[descripIndex] || '';
        const precio = precioIndex !== -1 ? parseFloat(values[precioIndex]) || 0 : 0;
        const tipoId = tipoIndex !== -1 ? values[tipoIndex] || '' : '';

        // Skip if no code or description
        if (!codigo && !descrip) {
          continue;
        }

        // Find tipo by TYPE ID - use existing tipo ID if it exists
        let tipo = '';
        if (tipoId) {
          // Check if tipo already exists in our map
          if (tiposMap[tipoId]) {
            tipo = tiposMap[tipoId];
            console.log(`Using existing tipo: ${tipoId} -> ${tipo}`);
          } else {
            // Create a new tipo if it doesn't exist
            try {
              const newTipo = await pb.collection('tipo').create({
                descrip: tipoId,
                sta: true
              });
              tipo = newTipo.id;
              // Add to map to avoid creating duplicates in the same import
              tiposMap[tipoId] = tipo;
              console.log(`Created new tipo: ${tipoId} -> ${tipo}`);
            } catch (tipoError) {
              console.warn(`Could not create tipo ${tipoId}:`, tipoError);
            }
          }
        }

        // Create the activo
        const activoData = {
          codigo: codigo,
          nombre: nombre,
          descrip: descrip,
          precio: precio,
          tipo: tipo,
          sta: true
        };

        let newActivo;
        try {
          newActivo = await pb.collection('activo').create(activoData);
        } catch (createError) {
          // Check if it's a duplicate code error
          if (createError.message && createError.message.includes('duplicate')) {
            throw new Error(`Duplicate code '${codigo}' already exists`);
          }
          throw new Error(`Failed to create activo: ${createError.message}`);
        }
        const activoId = newActivo.id;

        // Process additional columns as attributes (starting from column 6)
        const attributeColumns = headers.slice(5); // Skip first 5 columns (SKU/CODE, NAME, DESCRIPTION, PRICE, TYPE ID)
        const attributeValues = values.slice(5);

        for (let j = 0; j < attributeColumns.length; j++) {
          const attributeName = attributeColumns[j].trim();
          const attributeValue = attributeValues[j] ? attributeValues[j].trim() : '';

          // Skip empty attribute names or values
          if (!attributeName || !attributeValue) {
            continue;
          }

          try {
            // Find or create the attribute
            let atributoId = atributosMap[attributeName];
            if (!atributoId) {
              // Create new attribute
              const newAtributo = await pb.collection('atributo').create({
                descrip: attributeName,
                tipo_dato: 'texto', // Default to text type
                requerido: false,
                tipo: 'activo',
                sta: true
              });
              atributoId = newAtributo.id;
              atributosMap[attributeName] = atributoId; // Cache for future use
            }

            // Create the atributo_activo relation
            await pb.collection('atributo_activo').create({
              activo: activoId,
              atributo: atributoId,
              valor: attributeValue
            });

          } catch (attrError) {
            console.warn(`Error creating attribute ${attributeName} for activo ${codigo}:`, attrError);
          }
        }

        successCount++;

      } catch (lineError) {
        errorCount++;
        const detailedError = `Línea ${i + 1}: ${lineError.message}`;
        errors.push(detailedError);
        console.error(`Error processing line ${i + 1}:`, lineError);
        console.error(`Line data:`, values);
      }
    }

    return {
      success: true,
      successCount,
      errorCount,
      errors: errors.slice(0, 10) // Limit to first 10 errors
    };

  } catch (error) {
    console.error("Error importing activos from CSV:", error);
    throw error;
  }
}
